import type { ReactNode } from 'react';
import { Icon } from '@eti/components';
import { string, constants } from '@eti/utils';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { getBaggageWeightText } from '../utils/format';

interface TripDetailsBaggageIncludedProps {
  boundIncludedCheckedBaggage: { pieces: number; weight: number; weightUnit: string };
  segmentIncludedCheckedBaggage: { pieces: number; weight: number; weightUnit: string };
}

interface BaggageColumnProps {
  children: ReactNode;
}

interface TripDetailsSegmentBaggageTextProps {
  children: ReactNode;
}

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const tripDetailsSegmentBaggageTextStyles = css`
  color: #707070;
  font-size: 0.625rem;
  margin-left: 5px;
  padding-right: 20px;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 5px;
    padding-left: 20px;
    padding-right: 0;
  }

  @media ${mediaQueries.large.up} {
    font-size: 0.75rem;
    margin-left: 10px;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 10px;
    }
  }
`;

const baggageColumnStyles = css`
  align-items: center;
  bottom: 8px;
  display: flex;
  flex: 1 0 15%;
  padding-right: 10px;

  [dir='rtl'] & {
    padding-left: 10px;
    padding-right: 0;
  }

  & svg {
    flex-shrink: 0;
    height: 10px;
    width: 10px;
  }

  @media ${mediaQueries.medium.up} {
    align-items: flex-start;
    margin-top: 6px;
    position: relative;

    & svg {
      margin-top: 2px;
    }
  }

  @media ${mediaQueries.large.up} {
    margin-top: 10px;

    & svg {
      height: 14px;
      margin-top: 1px;
      width: 14px;
    }
  }
`;

export const BaggageColumn = ({ children }: BaggageColumnProps) => (
  <div className={baggageColumnStyles}>{children}</div>
);

export const TripDetailsSegmentBaggageText = ({
  children,
  ...rest
}: TripDetailsSegmentBaggageTextProps) => (
  <span className={tripDetailsSegmentBaggageTextStyles} {...rest}>
    {children}
  </span>
);

const TripDetailsBaggageIncluded = ({
  boundIncludedCheckedBaggage,
  segmentIncludedCheckedBaggage,
}: TripDetailsBaggageIncludedProps) => {
  const { t } = useTranslation();

  const weightText = `${LRE}${getBaggageWeightText(
    boundIncludedCheckedBaggage || segmentIncludedCheckedBaggage,
  )}${PDF}`;

  const content = boundIncludedCheckedBaggage
    ? string.insertArgument(t('TripDetails.BaggageIncludedBound'), weightText)
    : weightText;

  return (
    <BaggageColumn>
      <Icon color={etiColors.grayScale[8]} id="baggage-included" />
      <TripDetailsSegmentBaggageText data-testid="baggage-included-text">
        {content}
      </TripDetailsSegmentBaggageText>
    </BaggageColumn>
  );
};

export default TripDetailsBaggageIncluded;
